import React, { useState } from 'react';
import { useTheme } from "./ThemeContext";
import imageCompression from "browser-image-compression";

export default function ImageTools() {
    const { darkMode } = useTheme();
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [compressionLevel, setCompressionLevel] = useState(75);
    const [processingStatus, setProcessingStatus] = useState('');
    const [progress, setProgress] = useState(0);
    const [modifiedImage, setModifiedImage] = useState(null);
    const [convertFormat, setConvertFormat] = useState('png');

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
                setModifiedImage(null); // Reset modified preview
            };
            reader.readAsDataURL(file);
            setProcessingStatus('');
            setProgress(0);
        }
    };

    // Handle compression
    async function compressImage() {
        if (!imageFile) {
            setProcessingStatus('Please select an image first');
            return;
        }

        setProcessingStatus('Compressing...');
        setProgress(0);

        try {
            const options = {
                maxSizeMB: compressionLevel / 100, // Lower percentage means higher compression
                maxWidthOrHeight: 1024,
                useWebWorker: true,
                onProgress: (p) => setProgress(p), // Track compression progress
            };

            const compressedFile = await imageCompression(imageFile, options);
            const compressedURL = URL.createObjectURL(compressedFile);

            setModifiedImage(compressedURL);
            setProcessingStatus("Compression complete! Ready for download.");
        } catch (error) {
            console.error("Compression error:", error);
            setProcessingStatus("Error compressing image.");
        }
    }

    // Handle format conversion
    async function convertImage() {
        if (!imageFile) {
            setProcessingStatus('Please select an image first');
            return;
        }

        setProcessingStatus(`Converting to ${convertFormat.toUpperCase()}...`);
        setProgress(0);

        try {
            const options = {
                fileType: `image/${convertFormat}`,
                maxWidthOrHeight: 1024,
                useWebWorker: true,
                onProgress: (p) => setProgress(p),
            };

            const convertedFile = await imageCompression(imageFile, options);
            const convertedURL = URL.createObjectURL(convertedFile);

            setModifiedImage(convertedURL);
            setProcessingStatus(`Conversion to ${convertFormat.toUpperCase()} complete!`);
        } catch (error) {
            console.error("Conversion error:", error);
            setProcessingStatus("Error converting image.");
        }
    }

    // Handle downloading the modified image
    const downloadImage = () => {
        if (!modifiedImage) return;

        const link = document.createElement("a");
        link.href = modifiedImage;
        link.download = `modified_image.${convertFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={`mb-2 container mx-auto p-8 rounded-xl ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white'} shadow-lg`}>
            <h2 className="text-3xl font-bold mb-8">Image Tools</h2>

            {/* Upload Section */}
            <div className="mb-8">
                <label className={`inline-block mb-4 px-6 py-3 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-blue-50'} font-medium cursor-pointer transition-colors hover:bg-blue-100`}>
                    <span className="mr-2">Upload Image</span>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                </label>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Supported formats: JPG, PNG, WebP, GIF, BMP, TIFF, ICO, AVIF
                </p>
            </div>

            {/* Image Preview */}
            {imagePreview && (
                <div className="mb-8">
                    <h3 className="font-medium mb-3">Original Preview</h3>
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} flex justify-center`}>
                        <img src={imagePreview} alt="Preview" className="max-h-64 max-w-full object-contain rounded-lg" />
                    </div>
                </div>
            )}

            {/* Controls */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Format Conversion */}
                <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h3 className="font-medium mb-4 text-lg">Convert Format</h3>
                    <div className="flex mb-4">
                        <select
                            value={convertFormat}
                            onChange={(e) => setConvertFormat(e.target.value)}
                            className={`rounded-l-lg p-3 flex-grow ${darkMode ? 'bg-gray-600 text-white border-gray-600' : 'border border-gray-200'}`}
                        >
                            <option value="png">PNG</option>
                            <option value="jpg">JPG</option>
                            <option value="webp">WebP</option>
                            <option value="gif">GIF</option>
                            <option value="bmp">BMP</option>
                            <option value="tiff">TIFF</option>
                            <option value="ico">ICO</option>
                            <option value="avif">AVIF</option>
                        </select>
                        <button
                            onClick={convertImage}
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 rounded-r-lg hover:opacity-90 transition-opacity"
                        >
                            Convert
                        </button>
                    </div>
                </div>

                {/* Compression */}
                <div className={`rounded-xl p-6 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h3 className="font-medium mb-4 text-lg">Compression</h3>
                    <input
                        type="range"
                        min="1"
                        max="100"
                        value={compressionLevel}
                        onChange={(e) => setCompressionLevel(e.target.value)}
                        className="w-full accent-blue-500"
                    />
                    <button
                        onClick={compressImage}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 rounded-lg hover:opacity-90 transition-opacity w-full mt-4"
                    >
                        Compress
                    </button>
                </div>
            </div>

            {/* Progress Bar */}
            {progress > 0 && progress < 100 && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                </div>
            )}

            {/* Modified Image Preview & Download */}
            {modifiedImage && (
                <div className="mt-6">
                    <h3 className="font-medium mb-3">Modified Preview</h3>
                    <img src={modifiedImage} alt="Modified Preview" className="max-h-64 max-w-full object-contain rounded-lg mb-4" />
                    <button onClick={downloadImage} className="bg-green-500 text-white px-6 py-3 rounded-lg">
                        Download Modified Image
                    </button>
                </div>
            )}


            <h2 className="text-3xl font-bold mb-4">Image Tools Guide</h2>
            <div className="text-lg space-y-4">
                <h3 className="text-xl font-semibold">1. Upload Image</h3>
                <p>Select an image to upload. Supported formats include JPG, PNG, WebP, GIF, BMP, TIFF, ICO, and AVIF.</p>
                <h3 className="text-xl font-semibold">2. Image Preview</h3>
                <p>After uploading, preview your image before editing.</p>
                <h3 className="text-xl font-semibold">3. Format Conversion</h3>
                <p>Select a format (e.g., PNG, JPG, WebP) and click "Convert" to change the image format.</p>
                <h3 className="text-xl font-semibold">4. Compression</h3>
                <p>Adjust the compression level to reduce the file size and click "Compress" to apply changes.</p>
                <h3 className="text-xl font-semibold">5. Download Modified Image</h3>
                <p>Once you're satisfied with the image, click "Download Modified Image" to save it to your device.</p>
            </div>
        </div>

    );
}
